import z from 'zod';
import { FindProgrammingPlanQuery } from '../schema/ProgrammingPlan/FindProgrammingPlanOptions';
import { ProgrammingPlanLocalStatus } from '../schema/ProgrammingPlan/ProgrammingPlanLocalStatus';
import {
  ProgrammingPlanSettingsForm,
  ProgrammingSubPlanSettingsForm
} from '../schema/ProgrammingPlan/ProgrammingPlanSettingsForm';
import { ProgrammingPlanStatus } from '../schema/ProgrammingPlan/ProgrammingPlanStatus';
import {
  ProgrammingPlanChecked,
  ProgrammingPlanCreateInput
} from '../schema/ProgrammingPlan/ProgrammingPlans';
import {
  ProgrammingSubPlan,
  ProgrammingSubPlanId
} from '../schema/ProgrammingPlan/ProgrammingSubPlan';
import { ProgrammingSubPlanFieldConfig } from '../schema/SpecificData/ProgrammingSubPlanFieldConfig';
import type { SubRoutes } from './routes';

export const programmingPlansRoutes = {
  '/programming-plans': {
    get: {
      permissions: [
        'readProgrammingPlansInProgress',
        'readProgrammingPlanSubmittedToRegion',
        'readProgrammingPlanSubmittedToDepartments',
        'readProgrammingPlanValidated',
        'readProgrammingPlanClosed'
      ],
      query: FindProgrammingPlanQuery,
      response: z.array(ProgrammingPlanChecked)
    },
    post: {
      accountPermissions: ['manageProgrammingPlanSettings'],
      body: ProgrammingPlanCreateInput,
      response: ProgrammingPlanChecked
    }
  },
  '/programming-plans/send-to-regions': {
    post: {
      permissions: ['sendProgrammingPlansToRegions'],
      body: z.object({
        programmingPlanIds: z.array(z.guid()).min(1)
      }),
      response: z.array(ProgrammingPlanChecked)
    }
  },
  '/programming-plans/launch-campaign': {
    post: {
      permissions: ['launchProgrammingPlanCampaign'],
      body: z.object({
        programmingPlanIds: z.array(z.guid()).min(1)
      }),
      response: z.array(ProgrammingPlanChecked)
    }
  },
  '/programming-plans/send-to-departments': {
    post: {
      permissions: ['distributePrescriptionToDepartments'],
      body: z.object({
        programmingPlanIds: z.array(z.guid()).min(1)
      }),
      response: z.array(ProgrammingPlanChecked)
    }
  },
  '/programming-plans/send-to-samplers': {
    post: {
      permissions: ['distributePrescriptionToDepartments'],
      body: z.object({
        programmingPlanIds: z.array(z.guid()).min(1)
      }),
      response: z.array(ProgrammingPlanChecked)
    }
  },
  '/programming-plans/:programmingPlanId': {
    params: {
      programmingPlanId: z.guid()
    },
    get: {
      permissions: [
        'readProgrammingPlansInProgress',
        'readProgrammingPlanSubmittedToRegion',
        'readProgrammingPlanSubmittedToDepartments',
        'readProgrammingPlanValidated',
        'readProgrammingPlanClosed'
      ],
      response: ProgrammingPlanChecked
    },
    put: {
      permissions: ['manageProgrammingPlan', 'approveProgrammingPlan'],
      body: z.object({
        status: ProgrammingPlanStatus
      }),
      response: ProgrammingPlanChecked
    },
    delete: {
      accountPermissions: ['manageProgrammingPlanSettings'],
      response: z.undefined()
    }
  },
  '/programming-plans/:programmingPlanId/duplicate': {
    params: {
      programmingPlanId: z.guid()
    },
    post: {
      accountPermissions: ['manageProgrammingPlanSettings'],
      response: ProgrammingPlanChecked
    }
  },
  '/programming-plans/:programmingPlanId/sub-plans': {
    params: {
      programmingPlanId: z.guid()
    },
    post: {
      accountPermissions: ['manageProgrammingPlanSettings'],
      response: ProgrammingSubPlan
    }
  },
  '/programming-plans/:programmingPlanId/sub-plans/:programmingSubPlanId/duplicate':
    {
      params: {
        programmingPlanId: z.guid(),
        programmingSubPlanId: ProgrammingSubPlanId
      },
      post: {
        accountPermissions: ['manageProgrammingPlanSettings'],
        response: ProgrammingSubPlan
      }
    },
  '/programming-plans/:programmingPlanId/sub-plans/:programmingSubPlanId': {
    params: {
      programmingPlanId: z.guid(),
      programmingSubPlanId: ProgrammingSubPlanId
    },
    delete: {
      accountPermissions: ['manageProgrammingPlanSettings'],
      response: z.undefined()
    }
  },
  '/programming-plans/:programmingPlanId/local-status': {
    params: {
      programmingPlanId: z.guid()
    },
    put: {
      permissions: [
        'manageProgrammingPlan',
        'approveProgrammingPlan',
        'distributePrescriptionToDepartments',
        'distributePrescriptionToSlaughterhouses'
      ],
      body: z.object({
        programmingPlanLocalStatusList: z.array(ProgrammingPlanLocalStatus)
      }),
      response: ProgrammingPlanChecked
    }
  },
  '/programming-plans/:programmingPlanId/settings': {
    params: {
      programmingPlanId: z.guid()
    },
    get: {
      accountPermissions: ['manageProgrammingPlanSettings'],
      response: ProgrammingPlanSettingsForm
    },
    put: {
      accountPermissions: ['manageProgrammingPlanSettings'],
      body: ProgrammingPlanSettingsForm,
      response: z.undefined()
    }
  },
  '/programming-plans/:programmingPlanId/sub-plans/:programmingSubPlanId/settings':
    {
      params: {
        programmingPlanId: z.guid(),
        programmingSubPlanId: ProgrammingSubPlanId
      },
      get: {
        accountPermissions: ['manageProgrammingPlanSettings'],
        response: ProgrammingSubPlanSettingsForm
      },
      put: {
        accountPermissions: ['manageProgrammingPlanSettings'],
        body: ProgrammingSubPlanSettingsForm,
        response: z.undefined()
      }
    },
  '/programming-plans/:programmingPlanId/sub-plans/:programmingSubPlanId/specific-data-fields':
    {
      params: {
        programmingPlanId: z.string(),
        programmingSubPlanId: ProgrammingSubPlanId
      },
      get: {
        response: z.array(ProgrammingSubPlanFieldConfig),
        permissions: 'NONE'
      }
    }
} as const satisfies SubRoutes<'/programming-plans'>;
